import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import _ from "lodash";
import { useNavigate } from "react-router";
import { useChatApiClient } from "../../api/apiClients";
import {
  Bot,
  BotChat,
  Chat,
  FeedbackThumbs,
  IdResponse,
  Message,
  PDFChat,
} from "../../interfaces/chabot";
import { ObjectId } from "../../interfaces/common";

// Bots
const useBots = () => {
  const apiClient = useChatApiClient<Bot[]>();

  return useQuery({
    queryKey: ["bots"],
    queryFn: async () => await apiClient("GET", `/bot`),
    placeholderData: keepPreviousData,
  });
};

const useBot = (botId: ObjectId) => {
  const apiClient = useChatApiClient<Bot>();

  return useQuery({
    queryKey: ["bot", botId],
    queryFn: async () => await apiClient("GET", `/bot/${botId}`),
    placeholderData: keepPreviousData,
  });
};

// Create Chat
const useSendFirstMessage = () => {
  const apiClient = useChatApiClient<BotChat>();

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      chatId,
      content,
    }: {
      chatId: string;
      content: string;
    }) =>
      apiClient("POST", `/chat/${chatId}/message`, {
        content: content,
      }),
    onSuccess: (data, { chatId }) =>
      apiClient("PATCH", `/chat/${chatId}/title`),
    onSettled: async (data, error, { chatId }) => {
      queryClient.refetchQueries({ queryKey: ["chat", chatId] });
    },
  });
};

const useCreateChat = (bot: { id: string; name: string }) => {
  const apiClient = useChatApiClient<IdResponse>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { mutate } = useSendFirstMessage();

  return useMutation({
    mutationFn: async (content: string) =>
      apiClient("POST", `/bot/${bot.id}/chat`),
    onSuccess: (data, content) => {
      navigate(`/sirius/chat/${data.id}`, {
        state: {
          message: { content, updatedAt: Date.now() },
          bot: _.pick(bot, ["id", "name"]),
        },
      });
      mutate({ chatId: data.id, content });
    },
    onSettled: async () => {
      queryClient.refetchQueries({ queryKey: ["bot", bot.id] });
    },
  });
};

// Get Chat
const useChat = (chatId: string) => {
  const apiClient = useChatApiClient<BotChat>();

  return useQuery({
    queryKey: ["chat", chatId],
    queryFn: () => {
      return apiClient("GET", `/chat/${chatId}`);
    },
    placeholderData: keepPreviousData,
  });
};

// Delete Chat
const useDeleteChat = (botId: ObjectId) => {
  const apiClient = useChatApiClient<void>();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (chatId: ObjectId) => {
      return await apiClient("DELETE", `/chat/${chatId}`);
    },
    onMutate: async (chatId) => {
      await queryClient.cancelQueries({ queryKey: ["chat", chatId] });
      await queryClient.cancelQueries({ queryKey: ["bot", botId] });

      const previousHistory = queryClient.getQueryData(["bot", botId]);

      queryClient.setQueryData(
        ["bot", botId],
        (oldHistory: Bot | undefined) => {
          return oldHistory
            ? {
                ...oldHistory,
                chats: oldHistory.chats.filter((chat) => chat.id !== chatId),
              }
            : oldHistory;
        }
      );
      return { previousHistory };
    },
    onError: (err, chatId, context) => {
      console.log("Error while deleting chat: ", err);
      queryClient.setQueryData(["bot", botId], context?.previousHistory);
    },
    onSettled: (data, err, chatId, context) => {
      queryClient.invalidateQueries({ queryKey: ["chat", chatId] });
      queryClient.invalidateQueries({ queryKey: ["bot", botId] });
    },
  });
};

// Rename
const useRenameChat = (botId: ObjectId) => {
  const apiClient = useChatApiClient<Chat>();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      chatId,
      title,
    }: {
      chatId: ObjectId;
      title: string;
    }) => {
      return await apiClient("PATCH", `/chat/${chatId}/title/rename`, {
        title: title,
      });
    },
    onMutate: async ({ chatId, title }) => {
      await queryClient.cancelQueries({ queryKey: ["chat", chatId] });
      await queryClient.cancelQueries({ queryKey: ["bot", botId] });

      const previousHistory = queryClient.getQueryData(["bot", botId]);
      const previousChat = queryClient.getQueryData(["chat", chatId]);

      queryClient.setQueryData(
        ["bot", botId],
        (oldHistory: Bot | undefined) => {
          return oldHistory
            ? oldHistory.chats.map((chat) =>
                chat.id === chatId ? { ...chat, title: title } : chat
              )
            : oldHistory;
        }
      );
      queryClient.setQueryData(
        ["chat", chatId],
        (oldChat: BotChat | undefined) => {
          return oldChat ? { ...oldChat, title: title } : oldChat;
        }
      );
      return { previousHistory, previousChat };
    },
    onError: (err, variables, context) => {
      console.log("Error while renaming chat: ", err);
      queryClient.setQueryData(["bot", botId], context?.previousHistory);
      queryClient.setQueryData(
        ["chat", variables.chatId],
        context?.previousChat
      );
    },
    onSettled: (data, err, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["chat", variables.chatId] });
      queryClient.invalidateQueries({ queryKey: ["bot", botId] });
    },
  });
};

// Star
const useStarChat = (botId: ObjectId) => {
  const apiClient = useChatApiClient<Chat>();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      chatId,
      isFavorite,
    }: {
      chatId: ObjectId;
      isFavorite: boolean;
    }) => {
      return await apiClient("PATCH", `/chat/${chatId}/favourite`, {
        favourite: isFavorite,
      });
    },
    onMutate: async ({ chatId, isFavorite }) => {
      await queryClient.cancelQueries({ queryKey: ["chat", chatId] });
      await queryClient.cancelQueries({ queryKey: ["bot", botId] });

      const previousHistory = queryClient.getQueryData(["bot", botId]);
      const previousChat = queryClient.getQueryData(["chat", chatId]);

      queryClient.setQueryData(
        ["bot", botId],
        (oldHistory: Bot | undefined) => {
          return oldHistory
            ? oldHistory.chats.map((chat) =>
                chat.id === chatId ? { ...chat, favourite: isFavorite } : chat
              )
            : oldHistory;
        }
      );
      queryClient.setQueryData(
        ["chat", chatId],
        (oldChat: BotChat | undefined) => {
          return oldChat ? { ...oldChat, favourite: isFavorite } : oldChat;
        }
      );
      return { previousHistory, previousChat };
    },
    onError: (err, variables, context) => {
      console.log("Error while staring chat: ", err);
      queryClient.setQueryData(["bot", botId], context?.previousHistory);
      queryClient.setQueryData(
        ["chat", variables.chatId],
        context?.previousChat
      );
    },
    onSettled: (data, err, variables, context) => {
      queryClient.invalidateQueries({ queryKey: ["chat", variables.chatId] });
      queryClient.invalidateQueries({ queryKey: ["bot", botId] });
    },
  });
};

// Share Chat
const useShareChat = () => {
  const apiClient = useChatApiClient<Chat>();

  return useMutation({
    mutationFn: async (chatId: string) => {
      return apiClient("POST", `/chat/${chatId}/share`);
    },
    onError: (err) => {
      console.log("Error while sharing chat: ", err);
    },
  });
};

// Message
const useSendMessage = (chatId: string | undefined) => {
  const apiClient = useChatApiClient<BotChat>();

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (content: string) => {
      return await apiClient("POST", `/chat/${chatId}/message`, {
        content: content,
      });
    },
    onMutate: async (content) => {
      await queryClient.cancelQueries({ queryKey: ["chat", chatId] });

      const previousChat = queryClient.getQueryData(["chat", chatId]);

      queryClient.setQueryData(
        ["chat", chatId],
        (oldChat: BotChat | undefined) => {
          return oldChat
            ? {
                ...oldChat,
                messages: [
                  ...oldChat.messages,
                  {
                    id: "last",
                    content: content,
                    type: "human",
                    insertion_date: new Date(),
                  },
                ],
                updated_at: new Date(),
              }
            : oldChat;
        }
      );

      return { previousChat };
    },
    onError: (err, variables, context) => {
      console.error("Error while sending message: ", err);
      queryClient.setQueryData(["chat", chatId], context?.previousChat);
    },
    onSettled: async () => {
      queryClient.refetchQueries({ queryKey: ["chat", chatId] });
    },
  });
};

// Feedback
const useSendFeedback = (chatId: ObjectId | undefined) => {
  const apiClient = useChatApiClient<Message>();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      messageId,
      feedback,
    }: {
      messageId: ObjectId;
      feedback: FeedbackThumbs | null;
    }) =>
      apiClient("PATCH", `/chat/${chatId}/message/${messageId}/feedback`, {
        feedback: feedback,
      }),
    onMutate: async ({ messageId, feedback }) => {
      await queryClient.cancelQueries({ queryKey: ["chat", chatId] });

      const previousChat = queryClient.getQueryData(["chat", chatId]);

      queryClient.setQueryData(
        ["chat", chatId],
        (oldChat: BotChat | undefined) => {
          const newChat = oldChat
            ? {
                ...oldChat,
                messages: oldChat.messages.map((message) => {
                  return message.id === messageId
                    ? {
                        ...message,
                        feedback: feedback,
                      }
                    : message;
                }),
                updated_date: new Date(),
              }
            : oldChat;
          return newChat;
        }
      );
      return { previousChat };
    },
    onError: (err, variables, context) => {
      queryClient.setQueryData(["chat", chatId], context?.previousChat);
    },
    onSettled: async () => {
      queryClient.invalidateQueries({ queryKey: ["chat", chatId] });
    },
  });
};

// ChatPdf
const useChatPdf = (assetId: string) => {
  const apiClient = useChatApiClient<PDFChat>();

  return useQuery({
    queryKey: ["chatpdf", assetId],
    queryFn: async () =>
      apiClient("GET", `/chatpdf/chat`, undefined, {
        asset_id: assetId,
      }),
    placeholderData: keepPreviousData,
  });
};

// Message
const useSendMessagePdf = (assetId: ObjectId, chatId: ObjectId | undefined) => {
  const apiClient = useChatApiClient<BotChat>();

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      content,
      processing,
    }: {
      content: string;
      processing: any;
    }) => {
      return await apiClient("POST", `/chat/${chatId}/message`, {
        content: content,
        context: processing,
      });
    },
    onMutate: async ({ content, processing }) => {
      await queryClient.cancelQueries({ queryKey: ["chatpdf", assetId] });

      const previousChat = queryClient.getQueryData(["chatpdf", assetId]);

      queryClient.setQueryData(
        ["chatpdf", assetId],
        (oldChat: BotChat | undefined) => {
          return oldChat
            ? {
                ...oldChat,
                messages: [
                  ...oldChat.messages,
                  {
                    id: "last",
                    content: content,
                    type: "human",
                    insertion_date: new Date(),
                  },
                ],
                updated_at: new Date(),
              }
            : oldChat;
        }
      );

      return { previousChat };
    },
    onError: (err, variables, context) => {
      console.error("Error while sending message: ", err);
      queryClient.setQueryData(["chatpdf", assetId], context?.previousChat);
    },
    onSettled: async () => {
      queryClient.refetchQueries({ queryKey: ["chatpdf", assetId] });
    },
  });
};

// Feedback
const useSendFeedbackPdf = (
  assetId: ObjectId,
  chatId: ObjectId | undefined
) => {
  const apiClient = useChatApiClient<Message>();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      messageId,
      feedback,
    }: {
      messageId: ObjectId;
      feedback: FeedbackThumbs | null;
    }) =>
      apiClient("PATCH", `/chat/${chatId}/message/${messageId}/feedback`, {
        feedback: feedback,
      }),
    onMutate: async ({ messageId, feedback }) => {
      await queryClient.cancelQueries({ queryKey: ["chatpdf", assetId] });

      const previousChat = queryClient.getQueryData(["chatpdf", assetId]);

      queryClient.setQueryData(
        ["chatpdf", assetId],
        (oldChat: BotChat | undefined) => {
          const newChat = oldChat
            ? {
                ...oldChat,
                messages: oldChat.messages.map((message) => {
                  return message.id === messageId
                    ? {
                        ...message,
                        feedback: feedback,
                      }
                    : message;
                }),
                updated_date: new Date(),
              }
            : oldChat;
          return newChat;
        }
      );
      return { previousChat };
    },
    onError: (err, variables, context) => {
      queryClient.setQueryData(["chatpdf", assetId], context?.previousChat);
    },
    onSettled: async () => {
      queryClient.invalidateQueries({ queryKey: ["chatpdf", assetId] });
    },
  });
};

const useResetChatPdf = (assetId: ObjectId) => {
  const apiClient = useChatApiClient<IdResponse>();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () =>
      apiClient("POST", `/chatpdf/chat`, {
        asset_id: assetId,
      }),
    onMutate: async () =>
      queryClient.cancelQueries({ queryKey: ["chatpdf", assetId] }),
    onSettled: async () =>
      queryClient.invalidateQueries({ queryKey: ["chatpdf", assetId] }),
  });
};

export {
  useBot,
  useBots,
  useChat,
  useChatPdf,
  useCreateChat,
  useDeleteChat,
  useRenameChat,
  useResetChatPdf,
  useSendFeedback,
  useSendFeedbackPdf,
  useSendMessage,
  useSendMessagePdf,
  useShareChat,
  useStarChat,
};
